import React, { useState, useEffect, useMemo, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Button,
  Checkbox,
  IconButton,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  InputAdornment,
  TextField,
  Typography,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { Add, Edit, DeleteOutline } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickAnswersModal from "../../components/QuickAnswersModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
  buildWhatsappTabs,
  getWhatsappTabParams,
  orphanTabPrefix,
} from "../../utils/connectionsTabs";

const reducer = (state, action) => {
  if (action.type === "SET_QUICK_ANSWERS") {
    return action.payload;
  }

  if (action.type === "UPDATE_QUICK_ANSWERS") {
    const quickAnswer = action.payload;
    const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswer.id);

    if (quickAnswerIndex !== -1) {
      state[quickAnswerIndex] = quickAnswer;
      return [...state];
    } else {
      return [quickAnswer, ...state];
    }
  }

  if (action.type === "DELETE_QUICK_ANSWERS") {
    const quickAnswerId = action.payload;

    const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswerId);
    if (quickAnswerIndex !== -1) {
      state.splice(quickAnswerIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiTextField-root": {
        width: "100%",
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
      "& .MuiButton-label": {
        whiteSpace: "normal",
        lineHeight: 1.15,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  categorySection: {
    marginTop: theme.spacing(2),
    "&:first-of-type": {
      marginTop: theme.spacing(1),
    },
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  categoryTitle: {
    fontWeight: 600,
  },
  categoryCount: {
    color: theme.palette.text.secondary,
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCategorySection: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileShortcut: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileCategory: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  paginationBar: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center",
      "& .MuiTablePagination-toolbar": {
        flexWrap: "wrap",
        justifyContent: "center",
        paddingLeft: 0,
        paddingRight: 0,
      },
      "& .MuiTablePagination-spacer": {
        display: "none",
      },
    },
  },
}));

const QuickAnswers = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [quickAnswerCount, setQuickAnswerCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [quickAnswers, dispatch] = useReducer(reducer, []);
  const [selectedQuickAnswers, setSelectedQuickAnswers] = useState(null);
  const [quickAnswersModalOpen, setQuickAnswersModalOpen] = useState(false);
  const [deletingQuickAnswers, setDeletingQuickAnswers] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [selectedQuickAnswerIds, setSelectedQuickAnswerIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          quickAnswers
            .map(quickAnswer => quickAnswer.category || "General")
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [quickAnswers]
  );
  const groupedQuickAnswers = useMemo(
    () =>
      categories.map(category => ({
        category,
        quickAnswers: quickAnswers.filter(
          quickAnswer => (quickAnswer.category || "General") === category
        ),
      })),
    [categories, quickAnswers]
  );

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );
  const getQuickAnswerConnectionIds = quickAnswer =>
    quickAnswer.sharedConnectionIds?.length
      ? quickAnswer.sharedConnectionIds.map(Number)
      : quickAnswer.connectionId
      ? [Number(quickAnswer.connectionId)]
      : [];

  const quickAnswerOwnsTab = quickAnswer => {
    if (!selectedConnectionId) return true;
    const selectedValue = String(selectedConnectionId);

    if (selectedValue.startsWith(orphanTabPrefix)) {
      return String(quickAnswer.orphanedConnectionId || "") ===
        selectedValue.replace(orphanTabPrefix, "");
    }

    return getQuickAnswerConnectionIds(quickAnswer)
      .includes(Number(selectedConnectionId));
  };

  const quickAnswerMatchesSearch = quickAnswer => {
    const search = String(searchParam || "").trim().toLowerCase();
    if (!search) return true;

    return [
      quickAnswer?.shortcut,
      quickAnswer?.message,
      quickAnswer?.category,
    ].some(value => String(value || "").toLowerCase().includes(search));
  };

  const getQuickAnswerConnectionsLabel = quickAnswer => {
    const connectionIds = getQuickAnswerConnectionIds(quickAnswer);
    const names = connectionIds
      .map(connectionId => {
        const whatsapp = connections.find(item => Number(item.id) === Number(connectionId));
        return whatsapp?.name || whatsapp?.number;
      })
      .filter(Boolean);

    return names.length
      ? names.join(", ")
      : quickAnswer.connection?.name || quickAnswer.connection?.number || "-";
  };

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups")
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchConnections();
  }, []);

  useEffect(() => {
    setPageNumber(1);
    setSelectedQuickAnswerIds([]);
  }, [searchParam, selectedConnectionId]);

  useEffect(() => {
    const quickAnswerIds = new Set(
      quickAnswers.map(quickAnswer => String(quickAnswer.id))
    );
    setSelectedQuickAnswerIds(prevState =>
      prevState.filter(quickAnswerId =>
        quickAnswerIds.has(String(quickAnswerId))
      )
    );
  }, [quickAnswers]);

  useEffect(() => {
    setLoading(true);
    const fetchQuickAnswers = async () => {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: {
            searchParam,
            pageNumber,
            rowsPerPage,
            ...getWhatsappTabParams(selectedConnectionId)
          },
        });
        dispatch({ type: "SET_QUICK_ANSWERS", payload: data.quickAnswers });
        setQuickAnswerCount(data.count || 0);
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchQuickAnswers();
  }, [searchParam, pageNumber, rowsPerPage, selectedConnectionId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("quickAnswer", (data) => {
      if (data.action === "update" || data.action === "create") {
        if (quickAnswerOwnsTab(data.quickAnswer) &&
          quickAnswerMatchesSearch(data.quickAnswer)) {
          dispatch({ type: "UPDATE_QUICK_ANSWERS", payload: data.quickAnswer });
        } else if (data.action === "update") {
          dispatch({
            type: "DELETE_QUICK_ANSWERS",
            payload: +data.quickAnswer.id,
          });
        }
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_QUICK_ANSWERS",
          payload: +data.quickAnswerId,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [selectedConnectionId, searchParam]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
    setSelectedQuickAnswerIds([]);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPageNumber(1);
    setSelectedQuickAnswerIds([]);
  };

  const handleOpenQuickAnswersModal = () => {
    setSelectedQuickAnswers(null);
    setQuickAnswersModalOpen(true);
  };

  const handleCloseQuickAnswersModal = () => {
    setSelectedQuickAnswers(null);
    setQuickAnswersModalOpen(false);
  };

  const handleEditQuickAnswers = (quickAnswer) => {
    setSelectedQuickAnswers(quickAnswer);
    setQuickAnswersModalOpen(true);
  };

  const handleDeleteQuickAnswers = async (quickAnswerId) => {
    try {
      await api.delete(`/quickAnswers/${quickAnswerId}`);
      toast.success(i18n.t("quickAnswers.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickAnswers(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const allQuickAnswersSelected =
    quickAnswers.length > 0 &&
    quickAnswers.every(quickAnswer =>
      selectedQuickAnswerIds.includes(String(quickAnswer.id))
    );
  const selectedQuickAnswerCount = selectedQuickAnswerIds.length;

  const toggleQuickAnswerSelection = quickAnswerId => {
    const normalizedId = String(quickAnswerId);
    setSelectedQuickAnswerIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllQuickAnswersSelection = () => {
    if (allQuickAnswersSelected) {
      setSelectedQuickAnswerIds([]);
      return;
    }

    setSelectedQuickAnswerIds(quickAnswers.map(quickAnswer => String(quickAnswer.id)));
  };

  const handleDeleteSelectedQuickAnswers = async () => {
    try {
      await Promise.all(
        selectedQuickAnswerIds.map(quickAnswerId =>
          api.delete(`/quickAnswers/${quickAnswerId}`)
        )
      );
      selectedQuickAnswerIds.forEach(quickAnswerId =>
        dispatch({
          type: "DELETE_QUICK_ANSWERS",
          payload: Number(quickAnswerId),
        })
      );
      setSelectedQuickAnswerIds([]);
      toast.success(i18n.t("quickAnswers.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
      setSearchParam("");
      setPageNumber(1);
    }
  };

  const getQuickAnswerMessageLabel = quickAnswer => {
    if (quickAnswer.actionType === "sequence") {
      return "Secuencia";
    }

    if (quickAnswer.blocks?.length) {
      return `${quickAnswer.blocks.length} bloque(s)`;
    }

    return quickAnswer.message || "-";
  };

  const renderQuickAnswerActions = quickAnswer => (
    <>
      <IconButton
        size="small"
        onClick={() => handleEditQuickAnswers(quickAnswer)}
      >
        <Edit />
      </IconButton>

      <IconButton
        size="small"
        onClick={(e) => {
          setConfirmModalOpen(true);
          setDeletingQuickAnswers(quickAnswer);
        }}
      >
        <DeleteOutline />
      </IconButton>
    </>
  );

  const renderQuickAnswerRow = quickAnswer => (
    <TableRow key={quickAnswer.id}>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          color="primary"
          checked={selectedQuickAnswerIds.includes(String(quickAnswer.id))}
          onChange={() => toggleQuickAnswerSelection(quickAnswer.id)}
          inputProps={{
            "aria-label": `Seleccionar ${quickAnswer.shortcut}`,
          }}
        />
      </TableCell>
      <TableCell align="center">{quickAnswer.shortcut}</TableCell>
      <TableCell align="center">
        {getQuickAnswerConnectionsLabel(quickAnswer)}
      </TableCell>
      <TableCell align="center">
        {getQuickAnswerMessageLabel(quickAnswer)}
      </TableCell>
      <TableCell align="center">
        {renderQuickAnswerActions(quickAnswer)}
      </TableCell>
    </TableRow>
  );

  const renderMobileQuickAnswerCard = quickAnswer => (
    <div className={classes.mobileCard} key={quickAnswer.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedQuickAnswerIds.includes(String(quickAnswer.id))}
          onChange={() => toggleQuickAnswerSelection(quickAnswer.id)}
          inputProps={{
            "aria-label": `Seleccionar ${quickAnswer.shortcut}`,
          }}
        />
        <div>
          <Typography className={classes.mobileShortcut}>
            {quickAnswer.shortcut}
          </Typography>
          <Typography className={classes.mobileCategory}>
            {quickAnswer.category || "General"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>
          {renderQuickAnswerActions(quickAnswer)}
        </div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Conexión</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {getQuickAnswerConnectionsLabel(quickAnswer)}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("quickAnswers.table.message")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {getQuickAnswerMessageLabel(quickAnswer)}
          </Typography>
        </div>
      </div>
    </div>
  );

  const renderCategoryHeader = (category, count) => (
    <div className={classes.categoryHeader}>
      <Typography variant="subtitle2" className={classes.categoryTitle}>
        {category}
      </Typography>
      <Typography variant="body2" className={classes.categoryCount}>
        {count} respuesta{count === 1 ? "" : "s"}
      </Typography>
    </div>
  );

  const renderCategoryTable = ({ category, quickAnswers: categoryQuickAnswers }) => (
    <div className={classes.categorySection} key={category}>
      {renderCategoryHeader(category, categoryQuickAnswers.length)}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell align="center">
              {i18n.t("quickAnswers.table.shortcut")}
            </TableCell>
            <TableCell align="center">Conexión</TableCell>
            <TableCell align="center">
              {i18n.t("quickAnswers.table.message")}
            </TableCell>
            <TableCell align="center">
              {i18n.t("quickAnswers.table.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categoryQuickAnswers.map(renderQuickAnswerRow)}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickAnswers &&
          `${i18n.t("quickAnswers.confirmationModal.deleteTitle")} ${
            deletingQuickAnswers.shortcut
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickAnswers(deletingQuickAnswers.id)}
      >
        {i18n.t("quickAnswers.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedQuickAnswerCount} respuesta${
          selectedQuickAnswerCount === 1 ? "" : "s"
        } rápida${selectedQuickAnswerCount === 1 ? "" : "s"}?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedQuickAnswers}
      >
        {i18n.t("quickAnswers.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickAnswersModal
        open={quickAnswersModalOpen}
        onClose={handleCloseQuickAnswersModal}
        aria-labelledby="form-dialog-title"
        quickAnswerId={selectedQuickAnswers && selectedQuickAnswers.id}
        defaultConnectionId={selectedConnectionId}
        categories={categories}
      ></QuickAnswersModal>
      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("quickAnswers.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <TextField
              placeholder={i18n.t("quickAnswers.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
              onClick={handleOpenQuickAnswersModal}
            >
              {i18n.t("quickAnswers.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        {quickAnswers.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allQuickAnswersSelected}
              indeterminate={
                selectedQuickAnswerCount > 0 && !allQuickAnswersSelected
              }
              onChange={toggleAllQuickAnswersSelection}
              inputProps={{ "aria-label": "Seleccionar respuestas rápidas" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedQuickAnswerCount
                ? `${selectedQuickAnswerCount} seleccionada${
                    selectedQuickAnswerCount === 1 ? "" : "s"
                  }`
                : "Seleccionar respuestas rápidas"}
            </Typography>
            {selectedQuickAnswerCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedQuickAnswerIds([])}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutline />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar
                </Button>
              </>
            )}
          </div>
        )}
        <div className={classes.mobileCards}>
          {groupedQuickAnswers.map(({ category, quickAnswers: categoryQuickAnswers }) => (
            <div className={classes.mobileCategorySection} key={category}>
              {renderCategoryHeader(category, categoryQuickAnswers.length)}
              {categoryQuickAnswers.map(renderMobileQuickAnswerCard)}
            </div>
          ))}
          {loading && <CircularProgress size={24} />}
        </div>

        <div className={classes.tableWrapper}>
          {groupedQuickAnswers.map(renderCategoryTable)}
          {loading && (
            <Table size="small">
              <TableBody>
                <TableRowSkeleton columns={5} />
              </TableBody>
            </Table>
          )}
        </div>
        <div className={classes.paginationBar}>
          <TablePagination
            component="div"
            count={quickAnswerCount}
            page={pageNumber - 1}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 30, 50]}
            labelRowsPerPage="Respuestas por pagina"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
            }
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default QuickAnswers;

